import { useQuery } from '@tanstack/react-query';
import {
  faClipboardList,
  faHourglassHalf,
  faUserSlash,
  faRotateLeft,
  faCircleExclamation,
  faListCheck,
  faHeadset,
  faArrowRightArrowLeft,
  faHandshakeAngle,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '@/stores/auth.store';
import { dashboardService } from '@/services/dashboard.service';
import { KpiCard, KpiCardSkeleton } from './components/KpiCard';
import { QueueTable, QueueTableSkeleton } from './components/QueueTable';
import { RecentActivityFeed, RecentActivitySkeleton } from './components/RecentActivityFeed';
import { RequestCenterCard, RequestCenterCardSkeleton } from './components/RequestCenterCard';
import { AttentionRequired, AttentionRequiredSkeleton } from './components/AttentionRequired';
import { ROUTES } from '@/routes/routes';

/** Deep-link into the My Inspections tab whose filter produced a KPI's number. */
const tabTo = (tab: string) => `${ROUTES.MY_INSPECTIONS}?tab=${tab}`;

export function MasterEngineerDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.firstName ?? 'there';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard-master-engineer'],
    queryFn: dashboardService.getMasterEngineerDashboard,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Welcome back, {firstName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Team oversight and your personal assignments.
        </p>
      </div>

      {isError && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load dashboard. Check your connection and refresh.
        </div>
      )}

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          KPIs
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            Array.from({ length: 7 }).map((_, i) => <KpiCardSkeleton key={i} />)
          ) : data ? (
            <>
              <KpiCard
                label="All Open Inspections"
                value={data.kpis.allOpenInspections}
                to={ROUTES.TEAM_QUEUE}
                icon={faClipboardList}
              />
              <KpiCard
                label="My Active Assignments"
                value={data.kpis.myActiveAssignments}
                to={tabTo('in_progress')}
                icon={faHourglassHalf}
                iconCls="bg-amber-500/10 text-amber-700"
              />
              <KpiCard
                label="Unassigned (Team)"
                value={data.kpis.unassignedTeamInspections}
                to={ROUTES.JOB_POOL}
                icon={faUserSlash}
                iconCls="bg-destructive/10 text-destructive"
              />
              <KpiCard
                label="Returned Inspections"
                value={data.kpis.returnedInspections}
                to={ROUTES.TEAM_QUEUE}
                icon={faRotateLeft}
                iconCls="bg-red-500/10 text-red-700"
              />
              <KpiCard
                label="Escalated Cases"
                value={data.kpis.escalatedCases}
                icon={faCircleExclamation}
                iconCls="bg-orange-500/10 text-orange-700"
              />
              <KpiCard
                label="Checklist Approvals"
                value={data.kpis.checklistApprovals}
                icon={faListCheck}
                isFuture
              />
              <KpiCard
                label="Engineer Support Requests"
                value={data.kpis.engineerSupportRequests}
                icon={faHeadset}
                isFuture
              />
            </>
          ) : null}
        </div>
      </section>

      {/* Attention Required — real counts only; zero rows are not rendered. */}
      {isLoading ? (
        <AttentionRequiredSkeleton />
      ) : data ? (
        <AttentionRequired
          items={[
            {
              id: 'returned',
              icon: faRotateLeft,
              label: 'Returned inspections',
              hint: 'Returned to your team for revision',
              count: data.kpis.returnedInspections,
              to: ROUTES.TEAM_QUEUE,
              emphasis: true,
            },
            {
              id: 'escalated',
              icon: faCircleExclamation,
              label: 'Escalated cases',
              hint: 'Your inspections with a critical finding',
              count: data.kpis.escalatedCases,
              to: tabTo('in_progress'),
              emphasis: true,
            },
            {
              id: 'transfer',
              icon: faArrowRightArrowLeft,
              label: 'Transfer requests',
              hint: 'Transfers offered to you',
              count: data.requestCenter.transferRequests,
              to: ROUTES.REQUEST_CENTER,
            },
            {
              id: 'takeover',
              icon: faHandshakeAngle,
              label: 'Takeover requests',
              hint: 'Requests to take over your inspections',
              count: data.requestCenter.takeoverRequests,
              to: ROUTES.REQUEST_CENTER,
            },
            {
              id: 'authorization',
              icon: faShieldHalved,
              label: 'Authorization requests',
              hint: 'Cases waiting on your team to act',
              count: data.requestCenter.authorizationRequests,
              to: ROUTES.AUTHORIZATION_CENTER,
              emphasis: true,
            },
          ]}
        />
      ) : null}

      {/* Request Center */}
      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Requests
        </h2>
        {isLoading ? (
          <RequestCenterCardSkeleton />
        ) : data ? (
          <RequestCenterCard counts={data.requestCenter} />
        ) : null}
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {isLoading ? (
            <>
              <QueueTableSkeleton title="Unassigned Inspections" />
              <QueueTableSkeleton title="My Assignments" />
              <QueueTableSkeleton title="Returned to Me" />
            </>
          ) : data ? (
            <>
              <QueueTable
                title="Unassigned Inspections"
                items={data.queues.unassigned}
                viewAllTo={ROUTES.JOB_POOL}
                viewAllLabel="View job pool"
                emptyMessage="No unassigned inspections."
              />
              <QueueTable
                title="My Assignments"
                items={data.queues.myAssignments}
                viewAllTo={ROUTES.MY_INSPECTIONS}
                viewAllLabel="View all my inspections"
                showEngineer={false}
                emptyMessage="No active assignments."
              />
              <QueueTable
                title="Returned to Me"
                items={data.queues.returnedToMe}
                viewAllTo={tabTo('returned')}
                viewAllLabel="View all returned"
                showEngineer={false}
                emptyMessage="No returned inspections."
              />
            </>
          ) : null}
        </div>

        <div>
          {isLoading ? (
            <RecentActivitySkeleton />
          ) : data ? (
            <RecentActivityFeed items={data.recentActivity} />
          ) : null}
        </div>
      </section>
    </div>
  );
}
